import { Icon } from '@iconify/react'
import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import tw from 'twin.macro'

// Importação das duas imagens para alternância de tema
// Certifique-se que o caminho ./organisms/avatar/... está correto em relação a este arquivo
import avatarBranco from './organisms/avatar/branco.png'
import avatarPreto from './organisms/avatar/preto.png'

// Tipo da mensagem
type Message = {
    role: 'user' | 'bot'
    text: string
}

const TheChatBot = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'bot',
            text: 'Olá! Sou a IA do Jair. Pergunte-me sobre os projetos ou habilidades dele!',
        },
    ])
    const [inputValue, setInputValue] = useState('')
    const [loading, setLoading] = useState(false)

    // Estado para controlar o aviso inicial ("toast")
    // Começa false, o useEffect torna true ao montar, e depois false após 3s
    const [showToast, setShowToast] = useState(false)

    const messagesEndRef = useRef<HTMLDivElement>(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    // Efeito para mostrar a mensagem por 3 segundos assim que o componente carrega
    useEffect(() => {
        setShowToast(true) // Mostra o balão
        const timer = setTimeout(() => {
            setShowToast(false) // Esconde após 3 segundos
        }, 3000)

        return () => clearTimeout(timer) // Limpa o timer se o componente desmontar
    }, [])

    useEffect(() => {
        scrollToBottom()
    }, [messages, isOpen])

    const handleSend = async (e?: React.FormEvent) => {
        e?.preventDefault()
        if (!inputValue.trim() || loading) return

        const userText = inputValue
        setInputValue('')
        setLoading(true)

        const newHistory = [
            ...messages,
            { role: 'user', text: userText } as Message,
        ]

        setMessages(newHistory)

        try {
            const res = await fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: newHistory }),
            })

            const data = await res.json()
            setMessages((prev) => [...prev, { role: 'bot', text: data.reply }])
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                { role: 'bot', text: 'Ops, tive um erro de conexão.' },
            ])
        } finally {
            setLoading(false)
        }
    }

    return (
        <div tw="fixed bottom-8 right-8 z-50 hidden flex-col items-end space-y-4 md:flex">
            {isOpen && (
                <div
                    tw="
          mb-2 flex h-[calc(100vh-120px-120px)] 
          w-96 flex-col overflow-hidden rounded-2xl border
          border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900
        "
                >
                    {/* Header */}
                    <div tw="flex items-center justify-between bg-indigo-600 p-4 text-white">
                        <div tw="flex items-center space-x-3">
                            <div tw="flex size-8 items-center justify-center overflow-hidden rounded-full bg-white/20">
                                {/* Imagem do Header: Branco no modo claro, Preto no modo escuro */}
                                <img
                                    src={avatarBranco.src || avatarBranco}
                                    alt="Jair"
                                    tw="block size-full object-cover dark:hidden"
                                />
                                <img
                                    src={avatarPreto.src || avatarPreto}
                                    alt="Jair"
                                    tw="hidden size-full object-cover dark:block"
                                />
                            </div>
                            <div tw="flex flex-col">
                                <span tw="text-sm font-bold">Jair AI</span>
                                <span tw="flex items-center gap-1 text-xs text-indigo-200">
                                    <span tw="size-1.5 rounded-full bg-green-400" /> Online
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            tw="opacity-80 hover:text-gray-200 hover:opacity-100"
                        >
                            <Icon icon="heroicons-solid:x" tw="size-5" />
                        </button>
                    </div>

                    {/* Área das Mensagens */}
                    <div tw="flex-1 space-y-3 overflow-y-auto bg-gray-50 p-4 dark:bg-gray-950">
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                tw="max-w-[90%] p-3 text-sm leading-relaxed shadow-sm"
                                css={[
                                    msg.role === 'user'
                                        ? tw`bg-indigo-600 text-white self-end ml-auto rounded-2xl rounded-tr-none`
                                        : tw`bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 self-start rounded-2xl rounded-tl-none border border-gray-100 dark:border-gray-700`,
                                ]}
                            >
                                {/* --- RENDERIZADOR DE MARKDOWN --- */}
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        // Estilizando Links
                                        a: ({ href, children }) => (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="break-all font-bold underline hover:opacity-80"
                                            >
                                                {children}
                                            </a>
                                        ),
                                        // Estilizando Listas (Bolinhas)
                                        ul: ({ children }) => (
                                            <ul className="my-2 ml-4 list-disc space-y-1">
                                                {children}
                                            </ul>
                                        ),
                                        ol: ({ children }) => (
                                            <ol className="my-2 ml-4 list-decimal space-y-1">
                                                {children}
                                            </ol>
                                        ),
                                        // Estilizando Negrito
                                        strong: ({ children }) => (
                                            <strong className="font-bold text-indigo-500 dark:text-indigo-400">
                                                {children}
                                            </strong>
                                        ),
                                        // Estilizando Blocos de Código
                                        code: ({ children, className }) => {
                                            const isBlock = /language-/.test(className || '')
                                            return isBlock ? (
                                                <div className="my-2 overflow-x-auto rounded bg-gray-800 p-2 text-xs text-white">
                                                    <pre className={className || ''}>
                                                        <code>{children}</code>
                                                    </pre>
                                                </div>
                                            ) : (
                                                <code className="rounded bg-gray-200 px-1 py-0.5 text-xs font-bold text-red-500 dark:bg-gray-700 dark:text-red-400">
                                                    {children}
                                                </code>
                                            )
                                        },
                                    }}
                                >
                                    {msg.text}
                                </ReactMarkdown>
                            </div>
                        ))}
                        {loading && (
                            <div tw="ml-2 flex w-12 justify-center space-x-1 self-start rounded-lg bg-gray-200 p-2 dark:bg-gray-800">
                                <div tw="size-1.5 animate-bounce rounded-full bg-gray-500" />
                                <div tw="size-1.5 animate-bounce rounded-full bg-gray-500 delay-75" />
                                <div tw="size-1.5 animate-bounce rounded-full bg-gray-500 delay-150" />
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <form
                        onSubmit={handleSend}
                        tw="flex items-center gap-2 border-t border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900"
                    >
                        <input
                            tw="flex-1 rounded-full bg-gray-100 px-4 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Digite sua pergunta..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            autoFocus
                        />
                        <button
                            type="submit"
                            disabled={loading || !inputValue.trim()}
                            tw="rounded-full bg-indigo-600 p-2 text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Icon
                                icon="heroicons-solid:paper-airplane"
                                tw="size-4 rotate-90"
                            />
                        </button>
                    </form>
                </div>
            )}

            {/* Botão Trigger */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="group"
                tw="
          relative size-16 
          overflow-hidden rounded-full border-2 border-white shadow-xl
          ring-offset-2 transition-all duration-300
          hover:scale-105 hover:shadow-2xl focus:outline-none focus:ring-4
          focus:ring-indigo-500/50 dark:border-gray-700
        "
                aria-label="Abrir chat com IA"
            >
                {isOpen ? (
                    <div tw="flex size-full items-center justify-center bg-indigo-600 text-white">
                        <Icon icon="heroicons-solid:x" tw="size-8" />
                    </div>
                ) : (
                    <>
                        {/* Imagem do Botão Trigger: Branco no modo claro, Preto no modo escuro */}
                        <img
                            src={avatarBranco.src || avatarBranco}
                            alt="Falar com Jair"
                            tw="block size-full object-cover dark:hidden"
                        />
                        <img
                            src={avatarPreto.src || avatarPreto}
                            alt="Falar com Jair"
                            tw="hidden size-full object-cover dark:block"
                        />
                        <div
                            tw="
              pointer-events-none absolute right-20 top-1/2 
              -translate-y-1/2 whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1 text-xs
              text-white transition-opacity duration-300
            "
                            // LÓGICA DO BALÃOZINHO:
                            // Se showToast for true, opacidade 100.
                            // Se for false, opacidade 0, MAS volta a 100 se passar o mouse (group-hover).
                            css={[
                                showToast
                                    ? tw`opacity-100`
                                    : tw`opacity-0 group-hover:opacity-100`,
                            ]}
                        >
                            Converse com minha IA
                        </div>
                    </>
                )}
            </button>
        </div>
    )
}

export default TheChatBot
